import React from "react";
import Toggle from "../../Shared/Toggle";
import RadioGroup from "../../Shared/RadioGroup";
import PriceInput from "../../Shared/PriceInput";
import AddressLookupNative from "../../Shared/AddressLookupNative";
import { ADDRESS_TYPES } from "../../../config/availability";

/**
 * Shared travel-fee field group used by both TravelFeeForm (the dedicated
 * settings page) and ServiceForm (inline display when deliveryType includes
 * mobile).
 *
 * The caller owns the react-hook-form `register`, `watch`, and `setValue`
 * references and is responsible for persisting the values through the
 * existing `updateSettings` / `/booking-page/settings` PATCH path — the fee
 * is provider-level, not per-service.
 *
 * @param {Function}    register               - RHF register
 * @param {Function}    watch                  - RHF watch
 * @param {Function}    setValue               - RHF setValue
 * @param {Object|null} selectedAddress        - Currently selected base-location { id, name }
 * @param {Function}    onAddressSelect        - Called with { id, name } when address is chosen/cleared
 */
const TravelFeeFields = ({
  register,
  watch,
  setValue,
  selectedAddress,
  onAddressSelect,
}) => {
  const travelFeesEnabled = watch("travelFeesEnabled");
  const travelFeeType = watch("travelFeeType");
  const travelFeeDistanceUnit = watch("travelFeeDistanceUnit");

  const handleTravelFeesToggle = (checked) => {
    setValue("travelFeesEnabled", checked, { shouldDirty: true });
  };

  const distanceUnitOptions = [
    { id: "km", title: "km", description: "Rate per kilometer" },
    { id: "mi", title: "mi", description: "Rate per mile" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-ink-900">
            Enable travel fees
          </h3>
          <p className="text-sm text-ink-500">
            Charge additional fees for mobile services
          </p>
        </div>
        <Toggle checked={travelFeesEnabled} onChange={handleTravelFeesToggle} />
      </div>

      {travelFeesEnabled && (
        <div className="space-y-6">
          <div>
            <label className="text-sm font-medium text-ink-900">Fee type</label>
            <div className="mt-2 max-w-xs">
              <RadioGroup
                name="travelFeeType"
                options={[
                  { id: "flat", title: "Flat rate" },
                  { id: "distance", title: "Distance-based" },
                ]}
                register={register}
              />
            </div>
          </div>

          {travelFeeType === "flat" && (
            <div className="max-w-[180px]">
              <PriceInput
                label="Flat rate amount"
                type="number"
                step="0.01"
                min="0"
                {...register("travelFeeFlatRate", {
                  valueAsNumber: true,
                  min: { value: 0, message: "Amount cannot be negative" },
                })}
              />
            </div>
          )}

          {travelFeeType === "distance" && (
            <>
              <div>
                <label className="text-sm font-medium text-ink-900">
                  Base location
                </label>
                <p className="mt-1 text-sm text-ink-500">
                  This is your starting point for calculating travel distances
                  and fees
                </p>
                <div className="mt-2">
                  <AddressLookupNative
                    onSelect={onAddressSelect}
                    placeholder="Search for your business location or starting point..."
                    addressType={ADDRESS_TYPES.ADDRESS}
                    defaultValue={selectedAddress}
                  />
                </div>
              </div>

              <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-x-6">
                <div className="flex-1 max-w-[180px]">
                  <PriceInput
                    label={`Rate per ${travelFeeDistanceUnit || "km"}`}
                    type="number"
                    step="0.01"
                    min="0"
                    {...register("travelFeePerDistance", {
                      valueAsNumber: true,
                      min: { value: 0, message: "Rate cannot be negative" },
                    })}
                  />
                  <p className="mt-2 text-sm text-ink-500">
                    This rate will be charged per{" "}
                    {travelFeeDistanceUnit || "km"} traveled from your base
                    location.
                  </p>
                </div>
                <div className="sm:pt-6 max-w-xs">
                  <RadioGroup
                    name="travelFeeDistanceUnit"
                    options={distanceUnitOptions}
                    register={register}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TravelFeeFields;
